import React, { useEffect, useMemo, useState, useRef, useReducer, useCallback } from "react";
import { customTheme, toTitleCase } from "../utils.js";
import SwitchSeriesType from "../graphComponents/DynamicChart.js";
import CustomLegend from "../multipurpose/CustomLegend.js";
import { ThemeProvider, Box, Tabs, Tab, Select, MenuItem } from "@mui/material";
import StatCard from "../informationCards/StatCard.js";
import Card from "../informationCards/InformationCard.js";
import { fetchFamilyInfo, fetchRockets } from "../data/rocketFamilies.js";
import { fetchLaunchesOnly } from "../data/launches.js";
import { fetchPads } from "../data/pads.js";
import { fetchSpacecraft, fetchSpacecraftFamilyInfo } from "../data/spacecraft.js";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { BoosterStatsPanel } from "./BoosterStats.js";
import "./RocketStats.css";
import "./BoosterStats.css";

const REUSE_SERIES_COLORS = {
  "Max Booster": "#238bb8",
  "Avg Booster": "#8067d8",
  "Max Fairing": "#d6a84a",
  "Max Stage 2": "#168f7d",
  "Avg Stage 2": "#596173",
  "Max Spacecraft": "#238bb8",
  "Avg Spacecraft": "#8067d8",
};

const REUSE_SERIES_ORDER = [
  "Max Booster",
  "Avg Booster",
  "Max Fairing",
  "Max Stage 2",
  "Avg Stage 2",
  "Max Spacecraft",
  "Avg Spacecraft",
];
const REUSE_BOARD_FAMILIES = new Set(["falcon", "starship", "dragon"]);
const PAD_TEMPO_COLORS = {
  "SLC-40": "#238bb8",
  "LC-39A": "#d6a84a",
  "SLC-4E": "#8067d8",
  default: "#596173",
};
const PAD_TEMPO_ORDER = ["SLC-40", "LC-39A", "SLC-4E"];
const DRAGON_TYPE_COLORS = {
  Cargo: "#238bb8",
  Crew: "#8067d8",
  default: "#596173",
};
const DRAGON_STATUS_COLORS = {
  ACTIVE: "#238bb8",
  RETIRED: "#596173",
  LOST: "#d6a84a",
};
const DRAGON_STATUS_ORDER = ["ACTIVE", "RETIRED", "LOST"];

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatEmpty = (value, fallback = "TBD") =>
  value === null || value === undefined || value === "" ? fallback : value;

const formatEnum = (value) => toTitleCase(String(formatEmpty(value, "TBD")).replace(/_/g, " ")).replace("Tbd", "TBD");

const formatPercent = (value, total) => {
  const denominator = toNumber(total);
  if (!denominator) {
    return "TBD";
  }
  return `${Math.round((toNumber(value) / denominator) * 100)}%`;
};

const formatDateInputValue = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : "";
};

const formatRangeLabel = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.format("MMM YYYY") : "TBD";
};

const formatChartValue = (value) => {
  const number = toNumber(value);
  return Number.isInteger(number) ? String(number) : number.toFixed(1);
};

const formatAverage = (value) => {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number.toFixed(1) : "TBD";
};

const formatOrbitDays = (value) => {
  const parsed = Number.parseFloat(String(formatEmpty(value, "")).replace(/,/g, ""));
  return Number.isFinite(parsed) ? `${Math.round(parsed).toLocaleString()}d` : formatEmpty(value);
};

const secondsToDays = (value) => toNumber(value) / 86400;

const formatDays = (value) => {
  if (!Number.isFinite(value)) {
    return "TBD";
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)}d`;
};

const median = (values) => {
  if (!values.length) {
    return null;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

const parseChildrenStats = (childrenStats) =>
  Object.fromEntries(
    Object.entries(childrenStats).map(([rocketName, stats]) => {
      if (typeof stats === "object" && stats !== null) {
        return [rocketName, stats];
      }

      try {
        return [rocketName, JSON.parse(String(stats).replace(/'/g, '"'))];
      } catch {
        return [rocketName, {}];
      }
    })
  );

const sumChildStat = (parsedChildrenStats, key) =>
  Object.values(parsedChildrenStats).reduce((total, stats) => total + toNumber(stats[key]), 0);

const getChildStatsList = (parsedChildrenStats) =>
  Object.entries(parsedChildrenStats).map(([name, stats]) => ({ name, stats }));

const getPadName = (pad, fallback) => formatEmpty(pad?.nickname || pad?.name, fallback);

const isDateInRange = (date, start, end) =>
  date.valueOf() >= start.valueOf() && date.valueOf() <= end.valueOf();

const pluralizeLaunch = (count) => `${count} ${count === 1 ? "launch" : "launches"}`;

const formatLaunchTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "TBD";
  }

  return `${new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  })
    .format(date)
    .replace(/, (?=\d{2}:)/, " ")} UTC`;
};

const formatMass = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Unknown";
  }

  const mass = toNumber(value);
  return Number.isFinite(mass) ? `${mass.toLocaleString()} kg` : "Unknown";
};

const getStarshipLaunchRows = (launches, rocketIds, pads, selectedDate) => {
  const selectedStartDate = dayjs(selectedDate);
  const padLookup = new Map(pads.map((pad) => [String(pad.id), pad]));
  const familyRocketIds = new Set([...rocketIds].map(String));

  return launches
    .filter((launch) => familyRocketIds.has(String(launch.rocket)))
    .filter((launch) => {
      const launchDate = dayjs(launch.time);
      return launchDate.isValid() && !(selectedStartDate.isValid() && launchDate.isBefore(selectedStartDate, "day"));
    })
    .sort((a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf())
    .map((launch, index) => {
      const date = dayjs(launch.time);

      return {
        id: launch.id,
        customer: formatEmpty(launch.customer, "Unknown"),
        date,
        dateLabel: formatLaunchTime(launch.time),
        flightLabel: `F${index + 1}`,
        massLabel: formatMass(launch.mass),
        name: formatEmpty(launch.name, "Unknown mission"),
        padLabel: getPadName(padLookup.get(String(launch.pad)), "Unknown"),
      };
    });
};

const getStarshipSpotlightLaunch = (launches) => {
  const now = dayjs();
  const upcoming = launches.filter((launch) => launch.date.isAfter(now)).sort((a, b) => a.date.valueOf() - b.date.valueOf());

  if (upcoming.length) {
    return { ...upcoming[0], statusLabel: "Next up" };
  }

  const latest = [...launches].sort((a, b) => b.date.valueOf() - a.date.valueOf())[0];
  return latest ? { ...latest, statusLabel: "Latest mission" } : null;
};

const getPadTempoData = (launches, rocketIds, pads, selectedDate) => {
  const selectedStartDate = dayjs(selectedDate);
  const now = new Date();
  const padLookup = new Map(pads.map((pad) => [String(pad.id), pad]));
  const familyRocketIds = new Set([...rocketIds].map(String));

  const relevantLaunches = launches
    .filter((launch) => familyRocketIds.has(String(launch.rocket)))
    .filter((launch) => {
      const launchDate = dayjs(launch.time);
      if (!launchDate.isValid() || launchDate.toDate() > now) {
        return false;
      }

      return !(selectedStartDate.isValid() && launchDate.isBefore(selectedStartDate, "day"));
    })
    .sort((a, b) => dayjs(a.time).valueOf() - dayjs(b.time).valueOf());

  const years = [...new Set(relevantLaunches.map((launch) => dayjs(launch.time).year()))]
    .sort((a, b) => a - b)
    .slice(-6);

  if (!years.length) {
    return { lanes: [], years: [], start: null, end: null };
  }

  const start = dayjs(`${years[0]}-01-01`).startOf("year");
  const end = dayjs(`${years[years.length - 1]}-12-31`).endOf("year");
  const lanesByPad = new Map();

  relevantLaunches.forEach((launch) => {
    const launchDate = dayjs(launch.time);
    if (!isDateInRange(launchDate, start, end)) {
      return;
    }

    const padId = String(launch.pad);
    const padName = getPadName(padLookup.get(padId), `Pad ${padId}`);
    const padTurnaround = secondsToDays(launch.pad_turnaround);
    const lane =
      lanesByPad.get(padId) ||
      {
        id: padId,
        label: padName,
        color: PAD_TEMPO_COLORS[padName] || PAD_TEMPO_COLORS.default,
        launches: [],
        monthBuckets: new Map(),
        turnarounds: [],
      };
    const monthKey = launchDate.format("YYYY-MM");
    const bucket =
      lane.monthBuckets.get(monthKey) ||
      {
        id: `${padId}-${monthKey}`,
        date: launchDate.startOf("month").add(15, "day"),
        dateLabel: launchDate.format("MMM YYYY"),
        year: launchDate.year(),
        month: launchDate.month(),
        launchCount: 0,
        missions: [],
        turnarounds: [],
      };

    lane.launches.push({
      id: launch.id,
      name: formatEmpty(launch.name, "Unknown mission"),
      date: launchDate,
      dateLabel: launchDate.format("MMM D, YYYY"),
      padTurnaround: padTurnaround > 0 ? padTurnaround : null,
    });
    bucket.launchCount += 1;
    bucket.missions.push(formatEmpty(launch.name, "Unknown mission"));

    if (padTurnaround > 0) {
      lane.turnarounds.push(padTurnaround);
      bucket.turnarounds.push(padTurnaround);
    }

    lane.monthBuckets.set(monthKey, bucket);
    lanesByPad.set(padId, lane);
  });

  return {
    start,
    end,
    years,
    lanes: [...lanesByPad.values()]
      .map((lane) => ({
        id: lane.id,
        label: lane.label,
        color: lane.color,
        launches: lane.launches,
        count: lane.launches.length,
        medianTurnaround: median(lane.turnarounds),
        months: [...lane.monthBuckets.values()].map((bucket) => ({
          ...bucket,
          medianTurnaround: median(bucket.turnarounds),
        })),
      }))
      .sort((a, b) => {
        const aOrder = PAD_TEMPO_ORDER.indexOf(a.label);
        const bOrder = PAD_TEMPO_ORDER.indexOf(b.label);
        if (aOrder !== -1 || bOrder !== -1) {
          return (aOrder === -1 ? 999 : aOrder) - (bOrder === -1 ? 999 : bOrder);
        }
        return b.count - a.count;
      }),
  };
};

const getRecordObjects = (boosters, secondStages) => {
  const seen = new Set();
  return [
    ...boosters.map((object) => ({ ...object, recordType: "Booster" })),
    ...secondStages.map((object) => ({ ...object, recordType: "Stage 2" })),
  ]
    .filter((object) => {
      const key = `${object.recordType}-${object.id || object.name}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
    .sort((a, b) => toNumber(b.num_launches) - toNumber(a.num_launches))
    .slice(0, 5);
};

const getSpacecraftRecords = (spacecraft = [], fallbackSpacecraft = []) => {
  const seen = new Set();
  return [...spacecraft, ...fallbackSpacecraft]
    .filter((object) => {
      const key = object.id || object.name;
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
    .map((object) => ({ ...object, recordType: formatEnum(object.type || "Spacecraft") }))
    .sort((a, b) => toNumber(b.num_launches) - toNumber(a.num_launches))
    .slice(0, 5);
};

const getDragonStatusCounts = (spacecraft) =>
  DRAGON_STATUS_ORDER.map((status) => ({
    color: DRAGON_STATUS_COLORS[status] || DRAGON_STATUS_COLORS.RETIRED,
    count: spacecraft.filter((object) => object.status === status).length,
    label: formatEnum(status),
  })).filter((entry) => entry.count > 0);

const ReuseMetricStrip = ({ metrics }) => {
  const mainMetrics = metrics.slice(0, 3);
  const sideMetrics = metrics.slice(3);

  return (
    <div className="rs-metric-strip">
      <div className="rs-metric-group rs-metric-group-main">
        {mainMetrics.map((metric) => (
          <div className="rs-metric" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </div>
        ))}
      </div>
      <div className="rs-metric-group rs-metric-group-side">
        {sideMetrics.map((metric) => (
          <div className="rs-metric" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReuseLegend = ({ series }) => (
  <div className="rs-legend">
    {series.map((item) => (
      <span key={item.label}>
        <i style={{ backgroundColor: item.color }} />
        {item.label}
      </span>
    ))}
  </div>
);

const ReuseRangeControl = ({ startDate, chosenDate, onDateChange }) => {
  const start = dayjs(startDate);
  const now = dayjs();
  const selected = dayjs(chosenDate);
  const clampStart = (date) => {
    if (!date.isValid()) {
      return start.isValid() ? start : now.startOf("year");
    }

    if (start.isValid() && date.isBefore(start, "day")) {
      return start;
    }

    if (date.isAfter(now, "day")) {
      return now;
    }

    return date;
  };
  const rangesByValue = new Map();
  [
    { label: "All", value: start.isValid() ? start : now.startOf("year") },
    { label: "5Y", value: now.subtract(5, "year").startOf("year") },
    { label: "3Y", value: now.subtract(3, "year").startOf("year") },
    { label: "YTD", value: now.startOf("year") },
  ].forEach((range) => {
    const value = clampStart(range.value);
    const key = value.format("YYYY-MM-DD");

    if (!rangesByValue.has(key)) {
      rangesByValue.set(key, { ...range, value });
    }
  });
  const ranges = [...rangesByValue.values()];
  const selectedValue = selected.isValid() ? selected.format("YYYY-MM-DD") : "";
  const selectedLabel = formatRangeLabel(selected.isValid() ? selected : ranges[0].value);

  return (
    <div className="rs-range-control" aria-label="Chart date range">
      <div className="rs-range-control-head">
        <span>Range</span>
        <strong>{selectedLabel}</strong>
      </div>
      <div className="rs-range-options" style={{ "--range-count": ranges.length }}>
        {ranges.map((range) => {
          const value = range.value.format("YYYY-MM-DD");

          return (
            <button
              aria-pressed={selectedValue === value}
              className={selectedValue === value ? "active" : ""}
              key={range.label}
              onClick={() => onDateChange(value)}
              type="button"
            >
              {range.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ReuseChart = ({ launchYears, seriesData }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const chartSeries = REUSE_SERIES_ORDER.map((label) => seriesData.find((series) => series.label === label))
    .filter(Boolean)
    .map((series) => ({ ...series, color: REUSE_SERIES_COLORS[series.label] || series.color }));
  const allValues = chartSeries.flatMap((series) => series.data || []).map(toNumber);
  const maxValue = Math.max(1, ...allValues);
  const width = 720;
  const height = 240;
  const padding = { top: 22, right: 34, bottom: 42, left: 48 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const years = launchYears.length ? launchYears : [];
  const getX = (index) =>
    years.length <= 1 ? padding.left + plotWidth / 2 : padding.left + (index / (years.length - 1)) * plotWidth;
  const getY = (value) => padding.top + (1 - toNumber(value) / maxValue) * plotHeight;
  const yTicks = [0, maxValue / 2, maxValue];
  const xTickStep = years.length > 18 ? 2 : 1;
  const getTooltipItems = (index) =>
    chartSeries
      .map((series) => ({
        color: series.color,
        label: series.label,
        value: series.data?.[index],
      }))
      .filter((item) => item.value !== undefined && item.value !== null);
  const getTooltipPosition = (index) => {
    if (index < 2) {
      return { left: "0%", transform: "translateX(0)" };
    }

    if (index > years.length - 3) {
      return { left: "100%", transform: "translateX(-100%)" };
    }

    return { left: "50%", transform: "translateX(-50%)" };
  };
  const selectNearestYear = (clientX, target) => {
    if (!years.length) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / rect.width) * width;
    const clampedX = Math.max(padding.left, Math.min(width - padding.right, svgX));
    const index =
      years.length <= 1 ? 0 : Math.round(((clampedX - padding.left) / plotWidth) * (years.length - 1));

    setActiveIndex(index);
  };

  return (
    <div className="rs-chart-shell" onMouseLeave={() => setActiveIndex(null)}>
      <div className="rs-chart-plot">
        <svg
          className="rs-chart-svg"
          onMouseMove={(event) => selectNearestYear(event.clientX, event.currentTarget)}
          onTouchMove={(event) => selectNearestYear(event.touches[0].clientX, event.currentTarget)}
          onTouchStart={(event) => selectNearestYear(event.touches[0].clientX, event.currentTarget)}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Reuse growth by year"
        >
          <g className="rs-chart-grid">
            {yTicks.map((tick) => (
              <line key={tick} x1={padding.left} x2={width - padding.right} y1={getY(tick)} y2={getY(tick)} />
            ))}
            {years.map((year, index) => (
              <line key={year} x1={getX(index)} x2={getX(index)} y1={padding.top} y2={height - padding.bottom} />
            ))}
          </g>
          <g className="rs-chart-axis">
            {yTicks.map((tick) => (
              <text key={tick} x={padding.left - 14} y={getY(tick) + 4} textAnchor="end">
                {formatChartValue(tick)}
              </text>
            ))}
            {years.map((year, index) =>
              index % xTickStep === 0 || index === years.length - 1 ? (
                <text key={year} x={getX(index)} y={height - 14} textAnchor="middle">
                  '{String(year).slice(2)}
                </text>
              ) : null
            )}
          </g>
          {chartSeries.map((series) => {
            const points = (series.data || []).map((value, index) => `${getX(index)},${getY(value)}`).join(" ");

            return (
              <g className="rs-chart-series" key={series.label}>
                <polyline points={points} stroke={series.color} />
                {(series.data || []).map((value, index) => (
                  <circle
                    className={activeIndex === index ? "is-active" : undefined}
                    key={`${series.label}-${index}`}
                    cx={getX(index)}
                    cy={getY(value)}
                    r="4.5"
                    stroke={series.color}
                  />
                ))}
              </g>
            );
          })}
          {activeIndex !== null ? (
            <line
              className="rs-chart-active-line"
              x1={getX(activeIndex)}
              x2={getX(activeIndex)}
              y1={padding.top}
              y2={height - padding.bottom}
            />
          ) : null}
          {!chartSeries.length ? (
            <text className="rs-chart-empty" x={width / 2} y={height / 2} textAnchor="middle">
              Awaiting reuse data
            </text>
          ) : null}
        </svg>
        <div className="rs-chart-overlay" style={{ gridTemplateColumns: `repeat(${Math.max(1, years.length)}, 1fr)` }}>
          {years.map((year, index) => (
            <button
              aria-label={`${year} reuse data`}
              key={year}
              onBlur={() => setActiveIndex(null)}
              onClick={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
              onMouseEnter={() => setActiveIndex(index)}
              onTouchStart={() => setActiveIndex(index)}
              type="button"
            >
              <span aria-hidden="true" className="rs-chart-hover-card" style={getTooltipPosition(index)}>
                <strong>{year}</strong>
                {getTooltipItems(index).map((item) => (
                  <span key={item.label}>
                    <i style={{ backgroundColor: item.color }} />
                    {item.label}
                    <b>{formatChartValue(item.value)}</b>
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>
      </div>
      <ReuseLegend series={chartSeries} />
    </div>
  );
};

const PadTempoChart = ({ data }) => {
  const lanes = data?.lanes || [];
  const years = data?.years || [];
  const yearRows = years
    .map((year) => {
      const entries = lanes
        .map((lane) => {
          const buckets = (lane.months || []).filter((bucket) => bucket.year === year);
          const count = buckets.reduce((total, bucket) => total + bucket.launchCount, 0);
          const turnarounds = buckets.flatMap((bucket) => bucket.turnarounds || []);

          return {
            color: lane.color,
            count,
            label: lane.label,
            medianTurnaround: median(turnarounds),
            months: buckets.length,
          };
        })
        .filter((entry) => entry.count > 0);
      const total = entries.reduce((sum, entry) => sum + entry.count, 0);
      const leader = entries.reduce((best, entry) => (entry.count > (best?.count || 0) ? entry : best), null);

      return {
        entries: entries.map((entry) => ({
          ...entry,
          percent: total ? (entry.count / total) * 100 : 0,
        })),
        leader,
        total,
        year,
      };
    })
    .filter((row) => row.total > 0);
  const displayYearRows = [...yearRows].sort((a, b) => b.year - a.year);
  const totalLaunches = yearRows.reduce((total, row) => total + row.total, 0);

  return (
    <section className="rs-turnaround-panel">
      <div className="rs-turnaround-head">
        <div>
          <p className="rs-kicker">Pad activity</p>
          <h3>Pad launch mix</h3>
        </div>
        <div className="rs-turnaround-stats">
          <span>
            Window
            <strong>{years.length ? `${years[0]}-${years[years.length - 1]}` : "TBD"}</strong>
          </span>
          <span>
            Launches
            <strong>{totalLaunches || "TBD"}</strong>
          </span>
        </div>
      </div>
      {yearRows.length ? (
        <>
          <div className="rs-pad-mix-chart">
            {displayYearRows.map((row) => (
              <article className="rs-pad-mix-row" key={row.year}>
                <div className="rs-pad-mix-year">
                  <span>{row.year}</span>
                  <strong>{row.total}</strong>
                </div>
                <div className="rs-pad-mix-track" aria-label={`${row.year}: ${pluralizeLaunch(row.total)}`}>
                  {row.entries.map((entry) => (
                    <button
                      aria-label={`${row.year} ${entry.label}: ${pluralizeLaunch(entry.count)}, ${formatDays(
                        entry.medianTurnaround
                      )} median pad turn`}
                      className="rs-pad-mix-segment"
                      key={`${row.year}-${entry.label}`}
                      style={{ "--pad-color": entry.color, width: `${entry.percent}%` }}
                      type="button"
                    >
                      {entry.percent >= 18 ? <span>{entry.label}</span> : null}
                      <span className="rs-pad-mix-tooltip">
                        <strong>
                          {entry.label} / {row.year}
                        </strong>
                        <span>
                          Launches <b>{entry.count}</b>
                        </span>
                        <span>
                          Share <b>{Math.round(entry.percent)}%</b>
                        </span>
                        <span>
                          Median turn <b>{formatDays(entry.medianTurnaround)}</b>
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="rs-pad-mix-leader">
                  <span>Lead pad</span>
                  <strong>{row.leader?.label || "TBD"}</strong>
                </div>
              </article>
            ))}
          </div>
          <div className="rs-pad-mix-summary">
            {lanes.map((lane) => (
              <span key={lane.id} style={{ "--pad-color": lane.color }}>
                <i />
                <strong>{lane.label}</strong>
                <b>{lane.count}</b>
              </span>
            ))}
          </div>
        </>
      ) : (
        <p className="rs-empty">Awaiting pad tempo data</p>
      )}
    </section>
  );
};

const StarshipMissionPanel = ({ launch }) => (
  <section className="rs-starship-mission-panel">
    <div className="rs-turnaround-head">
      <div>
        <p className="rs-kicker">Mission focus</p>
        <h3>{launch?.statusLabel || "Mission context"}</h3>
      </div>
    </div>
    {launch ? (
      <article className="rs-starship-mission-card">
        <div>
          <span>{launch.dateLabel}</span>
          <strong>{launch.name}</strong>
        </div>
        <dl>
          <div>
            <dt>Pad</dt>
            <dd>{launch.padLabel}</dd>
          </div>
          <div>
            <dt>Customer</dt>
            <dd>{launch.customer}</dd>
          </div>
          <div>
            <dt>Payload</dt>
            <dd>{launch.massLabel}</dd>
          </div>
        </dl>
      </article>
    ) : (
      <p className="rs-empty">No Starship launch data available</p>
    )}
  </section>
);

const DragonFleetPanel = ({ childStatsList, spacecraft, launches, reuses }) => {
  const maxLaunches = Math.max(1, ...childStatsList.map(({ stats }) => toNumber(stats.Launches)));
  const statusCounts = getDragonStatusCounts(spacecraft);

  return (
    <section className="rs-dragon-panel">
      <div className="rs-turnaround-head">
        <div>
          <p className="rs-kicker">Mission profile</p>
          <h3>Cargo and crew mix</h3>
        </div>
        <div className="rs-turnaround-stats">
          <span>
            Capsules
            <strong>{spacecraft.length || "TBD"}</strong>
          </span>
          <span>
            Reflights
            <strong>{reuses || "TBD"}</strong>
          </span>
        </div>
      </div>
      <div className="rs-dragon-type-grid">
        {childStatsList.map(({ name, stats }) => {
          const typeLaunches = toNumber(stats.Launches);
          const typeReuses = toNumber(stats.Reuses);
          const color = DRAGON_TYPE_COLORS[name] || DRAGON_TYPE_COLORS.default;

          return (
            <article className="rs-dragon-type-card" key={name} style={{ "--dragon-color": color }}>
              <div className="rs-dragon-type-head">
                <div>
                  <span>{name}</span>
                  <strong>{typeLaunches}</strong>
                </div>
                <b>{formatPercent(typeReuses, typeLaunches)}</b>
              </div>
              <div className="rs-dragon-type-track">
                <span style={{ width: `${Math.max(6, (typeLaunches / maxLaunches) * 100)}%` }} />
              </div>
              <dl>
                <div>
                  <dt>Launches</dt>
                  <dd>{typeLaunches}</dd>
                </div>
                <div>
                  <dt>Reflights</dt>
                  <dd>{typeReuses}</dd>
                </div>
                <div>
                  <dt>Share</dt>
                  <dd>{formatPercent(typeLaunches, launches)}</dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>
      {statusCounts.length ? (
        <div className="rs-dragon-status-strip">
          {statusCounts.map((entry) => (
            <span key={entry.label} style={{ "--dragon-color": entry.color }}>
              <i />
              {entry.label}
              <b>{entry.count}</b>
            </span>
          ))}
        </div>
      ) : null}
    </section>
  );
};

const ReuseMaturityPanel = ({ family, launches, successes, landings, reuses, spacecraft = [] }) => {
  const isStarship = family === "starship";
  const isDragon = family === "dragon";
  const activeCapsules = spacecraft.filter((object) => object.status === "ACTIVE").length;
  const retiredCapsules = spacecraft.filter((object) => object.status === "RETIRED").length;
  const lostCapsules = spacecraft.filter((object) => object.status === "LOST").length;
  const averageFlightsPerCapsule = spacecraft.length ? launches / spacecraft.length : null;
  const successRate = formatPercent(successes, launches);
  const landingRate = formatPercent(landings, launches);
  const reuseLabel = `${reuses} ${reuses === 1 ? "reflight" : "reflights"}`;

  if (isDragon) {
    return (
      <section className="rs-card rs-dragon-fleet-card">
        <div className="rs-card-label">Capsule fleet</div>
        <div className="rs-dragon-fleet-stats">
          <div>
            <span>Active fleet</span>
            <strong>{spacecraft.length ? activeCapsules : "TBD"}</strong>
          </div>
          <div>
            <span>Retired fleet</span>
            <strong>{spacecraft.length ? retiredCapsules : "TBD"}</strong>
          </div>
          <div>
            <span>Lost capsules</span>
            <strong>{spacecraft.length ? lostCapsules : "TBD"}</strong>
          </div>
          <div>
            <span>Avg flights per capsule</span>
            <strong>{formatAverage(averageFlightsPerCapsule)}</strong>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rs-card">
      <div className="rs-card-label">Program maturity</div>
      <div className="rs-maturity-row">
        <span>Success rate</span>
        <strong>{successRate}</strong>
      </div>
      <div className="rs-progress">
        <span style={{ width: launches ? `${Math.min(100, (successes / launches) * 100)}%` : "0%" }} />
      </div>
      <div className="rs-maturity-row">
        <span>{isStarship ? "Recovery rate" : "Landing rate"}</span>
        <strong>{landingRate}</strong>
      </div>
      <div className="rs-progress">
        <span style={{ width: launches ? `${Math.min(100, (landings / launches) * 100)}%` : "0%" }} />
      </div>
      <div className="rs-maturity-note">
        <span>{isStarship ? "Hardware reflights" : "Reuse state"}</span>
        <strong>{reuseLabel}</strong>
      </div>
    </section>
  );
};

const ReuseObjectsPanel = ({ records, title = "Top objects" }) => {
  const maxFlights = Math.max(1, ...records.map((object) => toNumber(object.num_launches)));

  return (
    <section className="rs-card">
      <div className="rs-card-label">{title}</div>
      <div className="rs-object-bars">
        {records.length ? (
          records.map((object, index) => {
            const flights = toNumber(object.num_launches);
            const width = `${Math.max(6, (flights / maxFlights) * 100)}%`;

            return (
              <div className="rs-object-row" key={`${object.recordType}-${object.id}-${index}`}>
                <div>
                  <strong>{object.name}</strong>
                  <span>
                    {[object.nickname, object.recordType, formatEnum(object.status), object.version]
                      .filter(Boolean)
                      .join(" / ")}
                  </span>
                </div>
                <div className="rs-object-track">
                  <span style={{ width }} />
                </div>
                <b>x{flights}</b>
              </div>
            );
          })
        ) : (
          <p className="rs-empty">TBD</p>
        )}
      </div>
    </section>
  );
};

const ReuseRecordsPanel = ({
  family,
  latestLaunch,
  startDate,
  quickestBoosterTurnaround,
  quickestTurnaroundBooster,
  quickestSpacecraftTurnaround,
  quickestTurnaroundSpacecraft,
  topRecord,
}) => {
  const isDragon = family === "dragon";
  const rows = [
    ["First flight", formatDateInputValue(startDate) || "TBD"],
    latestLaunch ? ["Latest launch", latestLaunch.dateLabel] : null,
    isDragon && topRecord ? ["Most flown capsule", `${topRecord.name} x${toNumber(topRecord.num_launches)}`] : null,
    isDragon && quickestTurnaroundSpacecraft && quickestSpacecraftTurnaround
      ? ["Fastest capsule turn", quickestSpacecraftTurnaround]
      : null,
    !isDragon && quickestTurnaroundBooster && quickestBoosterTurnaround
      ? ["Fastest booster turn", quickestBoosterTurnaround]
      : null,
  ].filter(Boolean);

  return (
    <section className="rs-card">
      <div className="rs-card-label">Records</div>
      <dl className="rs-record-list">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const ReuseFamilyDetail = ({ displayName, childStatsList, family }) => {
  const hasMultipleChildren = childStatsList.length > 1;
  const isDragon = family === "dragon";
  const getFamilyRows = (stats) =>
    isDragon
      ? [
          ["Launches", formatEmpty(stats.Launches, "0")],
          ["Reflights", formatEmpty(stats.Reuses, "0")],
          ["Reuse rate", formatPercent(stats.Reuses, stats.Launches)],
        ]
      : [
          ["Launches", formatEmpty(stats.Launches, "0")],
          ["Successes", formatEmpty(stats.Successes, "0")],
          ["Flight proven", formatEmpty(stats["Flight Proven Launches"], "0")],
        ];

  return (
    <section className="rs-panel">
      <div className="rs-panel-head">
        <div>
          <p className="rs-kicker">{displayName} family</p>
          <h2>Fleet Details</h2>
        </div>
      </div>
      <div className={hasMultipleChildren ? "rs-family-grid" : "rs-detail-grid"}>
        {hasMultipleChildren
          ? childStatsList.map(({ name, stats }) => (
              <article className="rs-family-card" key={name}>
                <div className="rs-card-label">{name}</div>
                <div className="rs-family-card-stats">
                  {getFamilyRows(stats).map(([label, value]) => (
                    <React.Fragment key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </React.Fragment>
                  ))}
                </div>
              </article>
            ))
          : [
              [isDragon ? "Spacecraft Reflights" : "Booster Reflights", childStatsList[0]?.stats["Booster Reflights"]],
              [isDragon ? "Capsule Launches" : "2nd Stage Reflights", childStatsList[0]?.stats["2nd Stage Reflights"]],
              ["Flight Proven Launches", childStatsList[0]?.stats["Flight Proven Launches"]],
            ].map(([label, value]) => (
              <div className="rs-detail-stat" key={label}>
                <span>{label}</span>
                <strong>{formatEmpty(value, "0")}</strong>
              </div>
            ))}
      </div>
    </section>
  );
};

const ReuseProgramBoard = ({
  family,
  launchYears,
  seriesData,
  stats,
  parsedChildrenStats,
  startDate,
  chosenDate,
  onDateChange,
  mostFlownBoosters,
  mostFlownSecondStages,
  mostFlownSpacecraft,
  spacecraft,
  launchFeed,
  rockets,
  pads = [],
  quickestTurnaroundBooster,
  quickestBoosterTurnaround,
  quickestTurnaroundSpacecraft,
  quickestSpacecraftTurnaround,
  compactHeader = false,
}) => {
  const displayName = toTitleCase(family);
  const isStarship = family === "starship";
  const isDragon = family === "dragon";
  const childStatsList = getChildStatsList(parsedChildrenStats);
  const launchCount = toNumber(stats.Launches);
  const successes = sumChildStat(parsedChildrenStats, "Successes");
  const landings = toNumber(stats.Landings);
  const reuses = toNumber(stats.Reuses);
  const records = isDragon ? getSpacecraftRecords(spacecraft, mostFlownSpacecraft) : getRecordObjects(mostFlownBoosters, mostFlownSecondStages);
  const familyRocketNames = new Set(childStatsList.map(({ name }) => name));
  const familyRocketIds = new Set(rockets.filter((rocket) => familyRocketNames.has(rocket.name)).map((rocket) => rocket.id));
  const padTempoData = getPadTempoData(launchFeed, familyRocketIds, pads, chosenDate);
  const starshipLaunches = isStarship ? getStarshipLaunchRows(launchFeed, familyRocketIds, pads, chosenDate) : [];
  const starshipSpotlightLaunch = isStarship ? getStarshipSpotlightLaunch(starshipLaunches) : null;
  const latestStarshipLaunch = isStarship ? [...starshipLaunches].sort((a, b) => b.date.valueOf() - a.date.valueOf())[0] : null;
  const chartKicker = isDragon ? "Spacecraft reuse" : "Reuse profile";
  const chartTitle = isDragon ? "Capsule reuse by year" : "Reuse growth by year";
  const metrics = isDragon
    ? [
        { label: "Launches", value: launchCount },
        { label: "Reflights", value: reuses },
        { label: "Capsules", value: spacecraft.length },
        { label: "Orbit days", value: formatOrbitDays(stats["Time On Orbit:"]) },
      ]
    : [
        { label: "Launches", value: launchCount },
        { label: "Successes", value: successes },
        { label: isStarship ? "Recoveries" : "Landings", value: landings },
        { label: isStarship ? "Reflights" : "Reuses", value: reuses },
      ];
  const boardNote = isDragon
    ? `Capsules and missions since ${formatDateInputValue(startDate) || "debut"}`
    : `Launch and reuse objects since ${formatDateInputValue(startDate) || "debut"}`;

  return (
    <div className="rs-page">
      <section
        className={`rs-panel rs-program-board ${isStarship ? "rs-program-board--starship" : ""} ${
          isDragon ? "rs-program-board--dragon" : ""
        }`}
      >
        {compactHeader ? (
          <div className="rs-panel-head rs-panel-head--compact">
            <div>
              <p className="rs-kicker">Program</p>
              <h2>Launch and reuse metrics</h2>
            </div>
            <p className="rs-panel-note">{boardNote}</p>
          </div>
        ) : (
          <div className="rs-panel-head">
            <div>
              <p className="rs-kicker">{displayName} stats</p>
              <h1>{displayName} Program Stats</h1>
            </div>
            <p className="rs-panel-note">{boardNote}</p>
          </div>
        )}
        <ReuseMetricStrip metrics={metrics} />
        <div className="rs-dashboard-grid">
          <section className="rs-chart-panel">
            <div className="rs-chart-head">
              <div>
                <p className="rs-kicker">{chartKicker}</p>
                <h2>{chartTitle}</h2>
              </div>
              <ReuseRangeControl startDate={startDate} chosenDate={chosenDate} onDateChange={onDateChange} />
            </div>
            <ReuseChart launchYears={launchYears} seriesData={seriesData} />
            {isStarship ? <StarshipMissionPanel launch={starshipSpotlightLaunch} /> : null}
            {isDragon ? (
              <DragonFleetPanel
                childStatsList={childStatsList}
                spacecraft={spacecraft}
                launches={launchCount}
                reuses={reuses}
              />
            ) : null}
            {family === "falcon" ? <PadTempoChart data={padTempoData} /> : null}
          </section>
          <aside className="rs-side-stack">
            <ReuseMaturityPanel
              family={family}
              launches={launchCount}
              successes={successes}
              landings={landings}
              reuses={reuses}
              spacecraft={spacecraft}
            />
            <ReuseObjectsPanel records={records} title={isDragon ? "Top spacecraft" : isStarship ? "Top hardware" : "Top objects"} />
            <ReuseRecordsPanel
              family={family}
              latestLaunch={latestStarshipLaunch}
              startDate={startDate}
              quickestTurnaroundBooster={quickestTurnaroundBooster}
              quickestBoosterTurnaround={quickestBoosterTurnaround}
              quickestTurnaroundSpacecraft={quickestTurnaroundSpacecraft}
              quickestSpacecraftTurnaround={quickestSpacecraftTurnaround}
              topRecord={records[0]}
            />
          </aside>
        </div>
      </section>
      <ReuseFamilyDetail displayName={displayName} childStatsList={childStatsList} family={family} />
    </div>
  );
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ padding: "0" }}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
};

const FALCON_STATS_TABS = [
  { id: "program", label: "Program Stats" },
  { id: "boosters", label: "Booster Stats" },
];

const parseFalconStatsTab = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "boosters" || normalized === "booster" || normalized === "booster-stats") {
    return "boosters";
  }
  return "program";
};

const FalconStatsShell = ({ programBoard }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseFalconStatsTab(searchParams.get("tab"));
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));

  useEffect(() => {
    setVisitedTabs((current) => {
      if (current.has(activeTab)) {
        return current;
      }
      const next = new Set(current);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  const setActiveTab = useCallback(
    (tabId) => {
      const next = new URLSearchParams(searchParams);
      if (tabId === "program") {
        next.delete("tab");
      } else {
        next.set("tab", tabId);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="rs-stats-shell">
      <div className="rs-stats-shell-head">
        <div>
          <p className="rs-kicker">Falcon Stats</p>
          <h1>Falcon Stats</h1>
        </div>
        <p className="rs-stats-shell-note">
          Switch between booster fleet views and program-wide launch metrics.
        </p>
      </div>

      <div className="rs-stats-tabs" role="tablist" aria-label="Falcon stats sections">
        {FALCON_STATS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {visitedTabs.has("boosters") ? (
        <div
          className="rs-stats-tab-panel"
          role="tabpanel"
          id="falcon-stats-panel-boosters"
          aria-label="Booster Stats"
          hidden={activeTab !== "boosters"}
        >
          <BoosterStatsPanel embedded />
        </div>
      ) : null}
      {visitedTabs.has("program") ? (
        <div
          className="rs-stats-tab-panel"
          role="tabpanel"
          id="falcon-stats-panel-program"
          aria-label="Program Stats"
          hidden={activeTab !== "program"}
        >
          {programBoard}
        </div>
      ) : null}
    </div>
  );
};

const FamilyInformation = () => {
  const { family } = useParams();
  const [searchParams] = useSearchParams();
  const falconTab = family === "falcon" ? parseFalconStatsTab(searchParams.get("tab")) : "program";
  const skipNextDateFetch = useRef(false);
  const [launchYears, setLaunchYears] = useState([]);
  const [seriesData, setSeriesData] = useState([]);
  const [legendItems, setLegendItems] = useState([]);
  const [stats, setStats] = useState({});
  const [childrenStats, setChildrenStats] = useState({});
  const [rockets, setRockets] = useState([]);
  const [mostFlownBoosters, setMostFlownBoosters] = useState([]);
  const [mostFlownSecondStages, setMostFlownSecondStages] = useState([]);
  const [mostFlownSpacecraft, setMostFlownSpacecraft] = useState([]);
  const [spacecraft, setSpacecraft] = useState([]);
  const [launches, setLaunches] = useState([]);
  const [pads, setPads] = useState([]);
  const [quickestTurnaroundBooster, setQuickestTurnaroundBooster] = useState(null);
  const [quickestBoosterTurnaround, setQuickestBoosterTurnaround] = useState("");
  const [quickestTurnaroundSecondStage, setQuickestTurnaroundSecondStage] = useState(null);
  const [quickestSecondStageTurnaround, setQuickestSecondStageTurnaround] = useState("");
  const [quickestTurnaroundSpacecraft, setQuickestTurnaroundSpacecraft] = useState(null);
  const [quickestSpacecraftTurnaround, setQuickestSpacecraftTurnaround] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [chosenDate, setChosenDate] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const chartRef = useRef(null); // Ref to access the chart container

  // ensures responsive behavior of window
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Falcon booster tab does not need program metrics — skip the heavy
    // family/launch/pad fetch until the user actually opens Program Stats.
    if (family === "falcon" && falconTab === "boosters") {
      return undefined;
    }

    if (skipNextDateFetch.current) {
      skipNextDateFetch.current = false;
      return undefined;
    }

    const colors = ["#238bb8", "#8067d8", "#168f7d", "#596173"];

    const fetchData = async () => {
      let colorIndex = 0;
      let data;
      let rocketData;
      let launchData = [];
      let padData = [];
      let spacecraftData = null;

      if (family === "dragon") {
        [data, rocketData, spacecraftData] = await Promise.all([
          fetchSpacecraftFamilyInfo(family, chosenDate),
          fetchRockets(),
          fetchSpacecraft("", null, family),
        ]);
      } else {
        [data, rocketData, launchData, padData] = await Promise.all([
          fetchFamilyInfo(family, chosenDate),
          fetchRockets(),
          fetchLaunchesOnly({}),
          fetchPads("", {}),
        ]);
      }

      let legendItemData = [];
      const seriesList = Object.entries(data["series_data"] || {}).map(([key, value]) => {
        const seriesObject = {
          id: key,
          data: value,
          label: key,
          area: false,
          color: REUSE_SERIES_COLORS[key] || colors[colorIndex],
        };
        legendItemData.push({ color: REUSE_SERIES_COLORS[key] || colors[colorIndex], label: key });
        colorIndex = colorIndex + 1;
        return seriesObject;
      });

      setRockets(rocketData);
      setLaunches(launchData || []);
      setPads(padData || []);
      setSpacecraft(spacecraftData?.spacecraft || []);
      setLaunchYears(data["launch_years"] || []);
      setSeriesData(seriesList);
      setLegendItems(legendItemData);
      setStats(data["stats"] || {});
      setChildrenStats(data["children_stats"] || {});
      if (family === "dragon") {
        setMostFlownSpacecraft(data["spacecraft_with_most_flights"] || []);
        setQuickestTurnaroundSpacecraft(data["spacecraft_with_quickest_turnaround"]);
        setQuickestSpacecraftTurnaround(data["spacecraft_turnaround_time"]);
      } else {
        setMostFlownSpacecraft([]);
        setQuickestTurnaroundSpacecraft(null);
        setQuickestSpacecraftTurnaround("");
        setMostFlownBoosters(data["boosters_with_most_flights"] || []);
        setMostFlownSecondStages(data["stage_two_with_most_flights"] || []);
        setQuickestTurnaroundBooster(data["booster_with_quickest_turnaround"]);
        setQuickestBoosterTurnaround(data["booster_turnaround_time"]);
        setQuickestTurnaroundSecondStage(data["stage_two_with_quickest_turnaround"]);
        setQuickestSecondStageTurnaround(data["stage_two_turnaround_time"]);
      }
      setStartDate(data["start_date"] ? data["start_date"].slice(0, -1) : null);

      if (chosenDate === null && data["start_date"]) {
        skipNextDateFetch.current = true;
        setChosenDate(new Date(data["start_date"].slice(0, -1)));
      }
    };
    fetchData();
  }, [family, chosenDate, falconTab]);

  useEffect(() => {
    const handleResize = () => {
      forceUpdate();
    };

    // Add resize event listener
    window.addEventListener("resize", handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const parsedChildrenStats = useMemo(() => parseChildrenStats(childrenStats), [childrenStats]);

  const handleDateChange = (newDate) => {
    const date = dayjs(newDate);
    if (date.isValid()) {
      setChosenDate(date.toDate());
    }
  };

  if (REUSE_BOARD_FAMILIES.has(family)) {
    const programBoard = (
      <ReuseProgramBoard
        family={family}
        launchYears={launchYears}
        seriesData={seriesData}
        stats={stats}
        parsedChildrenStats={parsedChildrenStats}
        startDate={startDate}
        chosenDate={chosenDate}
        onDateChange={handleDateChange}
        launchFeed={launches}
        rockets={rockets}
        pads={pads}
        mostFlownBoosters={mostFlownBoosters}
        mostFlownSecondStages={mostFlownSecondStages}
        mostFlownSpacecraft={mostFlownSpacecraft}
        spacecraft={spacecraft}
        quickestTurnaroundBooster={quickestTurnaroundBooster}
        quickestBoosterTurnaround={quickestBoosterTurnaround}
        quickestTurnaroundSpacecraft={quickestTurnaroundSpacecraft}
        quickestSpacecraftTurnaround={quickestSpacecraftTurnaround}
        compactHeader={family === "falcon"}
      />
    );

    // Falcon: one Stats page with tabs for booster fleet vs program metrics
    if (family === "falcon") {
      return <FalconStatsShell programBoard={programBoard} />;
    }

    return programBoard;
  }

  return (
    <div className="mx-auto graph-container">
      <div style={{ width: "100%" }}>
        <h2 className="centered-heading">{toTitleCase(family)} Reuse Objects</h2>
        <div className="chart-and-stats" ref={chartRef}>
          <SwitchSeriesType
            seriesInput={seriesData}
            xAxisInput={[{ data: launchYears, scaleType: "band" }]}
            xAxisLabels={undefined}
            showxAxis={true}
            showAdditionalInfo={true}
            futureLaunchCount={0}
            displayStats={null}
            legend={<CustomLegend legendItems={legendItems} />}
            chartWidth={chartRef.current?.offsetWidth || 1000}
            onDateChange={handleDateChange}
            selectedDate={chosenDate}
            startDate={startDate}
          />
        </div>
      </div>
      <h2 className="centered-heading" style={{ margin: "60px 0 0 0" }}>
        Family Information
      </h2>
      <div className="stat-cards-container">
        {Object.entries(stats).map(([key, value]) => (
          <StatCard title={key} value={value} key={key} />
        ))}
      </div>
      {mostFlownBoosters.length > 0 ? (
        <>
          <h2 className="subheading" style={{ margin: "30px 0 0 0" }}>
            {mostFlownBoosters.length > 2 ? "Most Flown Boosters" : "Most Flown Booster"}
          </h2>
          <div className="launch-container launch-width">
            {mostFlownBoosters.map((booster) => (
              <Card
                key={booster.id}
                object={booster}
                objectType={"stage"}
                subObjects={{ rockets }}
                additionalInfo={{}}
              />
            ))}
          </div>
        </>
      ) : null}

      {quickestTurnaroundBooster ? (
        <>
          <h2 className="subheading">Quickest Booster Turnaround</h2>
          <div className="launch-container launch-width">
            <Card
              key={`${quickestTurnaroundBooster.id} fastest`}
              object={quickestTurnaroundBooster}
              objectType={"stage"}
              subObjects={{ rockets }}
              additionalInfo={{ "Fastest Turnaround: ": quickestBoosterTurnaround }}
            />
          </div>
        </>
      ) : null}

      {mostFlownSecondStages.length > 0 ? (
        <>
          <h2 className="subheading" style={{ margin: "30px 0 0 0" }}>
            {mostFlownSecondStages.length > 2 ? "Most Flown Second Stages" : "Most Flown Second Stage"}
          </h2>
          <div className="launch-container launch-width">
            {mostFlownSecondStages.map((stage) => (
              <Card key={stage.id} object={stage} objectType={"stage"} subObjects={{ rockets }} additionalInfo={{}} />
            ))}
          </div>
        </>
      ) : null}

      {quickestTurnaroundSecondStage ? (
        <>
          <h2 className="subheading">Quickest Second Stage Turnaround</h2>
          <div className="launch-container launch-width">
            <Card
              key={`${quickestTurnaroundSecondStage.id} fastest`}
              object={quickestTurnaroundSecondStage}
              objectType={"stage"}
              subObjects={{ rockets }}
              additionalInfo={{ "Fastest Turnaround: ": quickestSecondStageTurnaround }}
            />
          </div>
        </>
      ) : null}

      {mostFlownSpacecraft.length > 0 ? (
        <>
          <h2 className="subheading" style={{ margin: "30px 0 0 0" }}>
            Most Flown Spacecraft
          </h2>
          <div className="launch-container launch-width">
            {mostFlownSpacecraft.map((spacecraft) => (
              <Card key={spacecraft.id} object={spacecraft} objectType={"spacecraft"} additionalInfo={{}} />
            ))}
          </div>
        </>
      ) : null}

      {quickestTurnaroundSpacecraft ? (
        <>
          <h2 className="subheading">Quickest Spacecraft Turnaround</h2>
          <div className="launch-container launch-width">
            <Card
              key={`${quickestTurnaroundSpacecraft.id} fastest`}
              object={quickestTurnaroundSpacecraft}
              objectType={"spacecraft"}
              additionalInfo={{ "Fastest Turnaround: ": quickestSpacecraftTurnaround }}
            />
          </div>
        </>
      ) : null}

      <div className="launch-width">
        <ThemeProvider theme={customTheme}>
          <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
            {isMobileView ? (
              <Select
                value={tabValue}
                onChange={(event) => handleTabChange(null, event.target.value)}
                fullWidth
                style={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center" }}
              >
                {Object.keys(parsedChildrenStats).map((rocketName, index) => (
                  <MenuItem value={index} key={index}>
                    {rocketName}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="rocket stats tabs">
                {Object.keys(parsedChildrenStats).map((rocketName, index) => (
                  <Tab label={rocketName} key={index} />
                ))}
              </Tabs>
            )}
          </Box>
          {Object.entries(parsedChildrenStats).map(([rocketName, stats], index) => (
            <TabPanel value={tabValue} index={index} key={index}>
              <div className="stat-cards-container" style={{ width: "100%", paddingTop: "10px" }}>
                {Object.entries(stats).map(([key, value]) => (
                  <StatCard title={key} value={value} key={key} />
                ))}
              </div>
            </TabPanel>
          ))}
        </ThemeProvider>
      </div>
    </div>
  );
};

export default FamilyInformation;
